import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Calendar as CalendarIcon,
  CalendarDays,
  Loader2,
} from 'lucide-react'
import { api } from '../lib/api'
import WeekView from '../components/calendar/WeekView'
import MonthView from '../components/calendar/MonthView'
import EventFormModal from '../components/calendar/EventFormModal'
import {
  addDays,
  endOfMonth,
  endOfWeek,
  monthGridEnd,
  monthGridStart,
  monthLabel,
  rangeLabel,
  startOfMonth,
  startOfWeek,
} from '../lib/datetime'
import type { CalendarEventDto, CalendarListResponse } from '@toga/shared'

type View = 'week' | 'month'

export default function Agenda() {
  const [view, setView] = useState<View>('week')
  const [cursor, setCursor] = useState<Date>(new Date())
  const [events, setEvents] = useState<CalendarEventDto[]>([])
  const [loading, setLoading] = useState(false)
  const [editing, setEditing] = useState<CalendarEventDto | 'new' | null>(null)
  const [newSlot, setNewSlot] = useState<Date | null>(null)

  const range = useMemo(() => {
    if (view === 'week') {
      return { from: startOfWeek(cursor), to: endOfWeek(cursor) }
    }
    return { from: monthGridStart(cursor), to: monthGridEnd(cursor) }
  }, [view, cursor])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      params.set('from', range.from.toISOString())
      params.set('to', range.to.toISOString())
      const resp = await api.get<CalendarListResponse>(`/calendar-events?${params.toString()}`)
      setEvents(resp.items)
    } catch {
      setEvents([])
    } finally {
      setLoading(false)
    }
  }, [range])

  useEffect(() => {
    void load()
  }, [load])

  const onPrev = () => {
    setCursor((d) =>
      view === 'week' ? addDays(d, -7) : startOfMonth(addDays(startOfMonth(d), -1))
    )
  }
  const onNext = () => {
    setCursor((d) => (view === 'week' ? addDays(d, 7) : startOfMonth(addDays(endOfMonth(d), 1))))
  }
  const onToday = () => setCursor(new Date())

  const headerLabel =
    view === 'week'
      ? rangeLabel(startOfWeek(cursor), endOfWeek(cursor))
      : `${monthLabel(cursor)} ${cursor.getFullYear()}`

  return (
    <div className="space-y-4 animate-slideUp">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onPrev}
            className="w-8 h-8 grid place-items-center rounded-lg hover:bg-hover t-muted"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            type="button"
            onClick={onNext}
            className="w-8 h-8 grid place-items-center rounded-lg hover:bg-hover t-muted"
          >
            <ChevronRight size={16} />
          </button>
          <button
            type="button"
            onClick={onToday}
            className="px-3 py-1 rounded-lg text-xs t-secondary border border-base hover:bg-hover"
          >
            Hoy
          </button>
          <h2 className="ml-2 text-xl font-serif font-semibold t-primary capitalize">
            {headerLabel}
          </h2>
          {loading && <Loader2 size={14} className="animate-spin text-toga-300 ml-2" />}
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-base overflow-hidden">
            <button
              type="button"
              onClick={() => setView('week')}
              className={[
                'px-3 py-1.5 text-xs flex items-center gap-1.5',
                view === 'week' ? 'bg-toga-500/15 text-toga-200' : 't-secondary hover:bg-hover',
              ].join(' ')}
            >
              <CalendarDays size={14} /> Semana
            </button>
            <button
              type="button"
              onClick={() => setView('month')}
              className={[
                'px-3 py-1.5 text-xs flex items-center gap-1.5 border-l border-base',
                view === 'month' ? 'bg-toga-500/15 text-toga-200' : 't-secondary hover:bg-hover',
              ].join(' ')}
            >
              <CalendarIcon size={14} /> Mes
            </button>
          </div>
          <button
            type="button"
            onClick={() => {
              setNewSlot(null)
              setEditing('new')
            }}
            className="px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 hover:shadow-glow-toga flex items-center gap-1.5"
          >
            <Plus size={14} /> Nuevo evento
          </button>
        </div>
      </header>

      {view === 'week' ? (
        <WeekView
          cursor={cursor}
          events={events}
          onPickSlot={(d) => {
            setNewSlot(d)
            setEditing('new')
          }}
          onPickEvent={(e) => setEditing(e)}
        />
      ) : (
        <MonthView
          cursor={cursor}
          events={events}
          onPickDay={(d) => {
            const slot = new Date(d)
            slot.setHours(9, 0, 0, 0)
            setNewSlot(slot)
            setEditing('new')
          }}
          onPickEvent={(e) => setEditing(e)}
        />
      )}

      {editing !== null && (
        <EventFormModal
          initial={editing === 'new' ? null : editing}
          defaultStartAt={newSlot ?? undefined}
          onClose={() => {
            setEditing(null)
            setNewSlot(null)
          }}
          onSaved={() => {
            setEditing(null)
            setNewSlot(null)
            void load()
          }}
        />
      )}
    </div>
  )
}
